/**
 * Mode Selectors - Question Selection Strategies
 *
 * Mode-specific question selection logic.
 * Each selector implements a different strategy for choosing questions.
 *
 * NOTE: Simulation selector removed — merged with Assessment as "Toets" in Learning Lab
 */

import { Question, ModuleId, AppState } from '../types'
import { GroupId } from '../config/systemConfig'
import {
  FluencyConfig,
  SprintConfig,
  ChallengeConfig,
  ExperimentsConfig,
} from './types'
import { createRng } from '../utils/deterministicRng'

/**
 * Get weakest families for sprint mode
 */
export function getWeakestFamilies(
  state: AppState,
  limit: number = 3,
): Array<{
  familyId: string
  masteryScore: number
}> {
  const familyMastery = state.familyMastery || {}
  return Object.entries(familyMastery)
    .map(([familyId, mastery]) => ({
      familyId,
      masteryScore: mastery.masteryScore,
    }))
    .sort((a, b) => a.masteryScore - b.masteryScore)
    .slice(0, limit)
}

/**
 * Fluency Mode: Mixed practice from multiple modules
 * Focus on speed and accuracy with familiar content
 */
export function getFluencyQuestions(
  config: FluencyConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())
  const familyMastery = state.familyMastery || {}

  // Filter by group and modules
  let candidates = allQuestions.filter((q) => {
    if (q.group !== selectedGroup) return false
    if (config.targetModules && config.targetModules.length > 0) {
      return config.targetModules.includes(q.module)
    }
    return true
  })

  // Filter by tier range if specified
  if (config.tierRange) {
    candidates = candidates.filter(
      (q) => q.tier >= config.tierRange!.min && q.tier <= config.tierRange!.max,
    )
  }

  // Prioritize families with 50-80% mastery (fluency sweet spot)
  const fluencyFamilies = Object.entries(familyMastery)
    .filter(([_, m]) => m.masteryScore >= 50 && m.masteryScore <= 80)
    .map(([familyId]) => familyId)
  if (fluencyFamilies.length > 0) {
    const fluencyQuestions = candidates.filter((q) =>
      fluencyFamilies.includes(q.familyId),
    )
    if (fluencyQuestions.length > 0) {
      candidates = fluencyQuestions
    }
  }

  // Estimate questions based on duration (30 seconds per question)
  const estimatedCount = Math.floor(config.duration / 30)
  const targetCount = Math.min(estimatedCount, candidates.length, 50)

  // Shuffle and select
  const shuffled = candidates.sort(() => rng.random() - 0.5)
  return shuffled.slice(0, targetCount)
}

/**
 * Sprint Mode: Focused practice on weakest family
 * Intensive drilling on a single skill
 */
export function getSprintQuestions(
  config: SprintConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())

  // Filter by family and group
  let candidates = allQuestions.filter(
    (q) => q.familyId === config.targetFamilyId && q.group === selectedGroup,
  )
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`)
  }

  // If adaptive, start with easier questions
  if (config.adaptive) {
    candidates = candidates.sort((a, b) => a.tier - b.tier)
  } else {
    // Random order
    candidates = candidates.sort(() => rng.random() - 0.5)
  }

  // Select target count
  return candidates.slice(0, config.targetCount)
}

/**
 * Challenge Mode: Unlock new content
 * Progressive difficulty within a family
 */
export function getChallengeQuestions(
  config: ChallengeConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const familyMastery = state.familyMastery?.[config.targetFamilyId]

  // Filter by family and group
  let candidates = allQuestions.filter(
    (q) => q.familyId === config.targetFamilyId && q.group === selectedGroup,
  )
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`)
  }

  // Determine tier range based on difficulty and current mastery
  const currentTier =
    familyMastery?.attempts > 0
      ? Math.floor(familyMastery.masteryScore / 20) + 1
      : 1
  const tierOffset = config.difficulty - 1 // 0, 1, or 2
  const minTier = Math.max(1, currentTier + tierOffset)
  const maxTier = Math.min(16, minTier + 2)

  // Filter by tier range
  candidates = candidates.filter((q) => q.tier >= minTier && q.tier <= maxTier)

  // Sort by tier (progressive difficulty)
  candidates = candidates.sort((a, b) => a.tier - b.tier)

  // Select 10-15 questions
  const targetCount = 10 + Math.floor(Math.random() * 6)
  return candidates.slice(0, Math.min(targetCount, candidates.length))
}

/**
 * Experiments Mode: Special question types and features
 * For testing new content or specific practice
 */
export function getExperimentsQuestions(
  config: ExperimentsConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())

  // Filter by group
  let candidates = allQuestions.filter((q) => q.group === selectedGroup)

  // Apply experiment-specific filters
  switch (config.experimentType) {
    case 'visual_aids':
      // Questions with visual aids
      candidates = candidates.filter(
        (q) =>
          (q.stimulusAids && q.stimulusAids.length > 0) ||
          (q.supportAids && q.supportAids.length > 0),
      )
      break
    case 'interactive':
      // Interactive question types
      candidates = candidates.filter(
        (q) => q.questionType && !['multiple_choice'].includes(q.questionType),
      )
      break
    case 'new_content':
      // Recently added questions (last 30 days)
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
      candidates = candidates.filter((q) => {
        const questionState = state.questionStates[q.id]
        return !questionState || questionState.lastAttemptDate > thirtyDaysAgo
      })
      break
    case 'mixed':
    default:
      // Random selection
      break
  }

  // Shuffle and select 15-20 questions
  const shuffled = candidates.sort(() => rng.random() - 0.5)
  const targetCount = 15 + Math.floor(rng.random() * 6)
  return shuffled.slice(0, Math.min(targetCount, candidates.length))
}

/**
 * Get available challenge families
 * Returns families that have sufficient mastery to unlock challenges
 */
export function getAvailableChallengeFamilies(state: AppState): Array<{
  familyId: string
  masteryScore: number
  unlocked: boolean
}> {
  const familyMastery = state.familyMastery || {}
  return Object.entries(familyMastery)
    .map(([familyId, mastery]) => ({
      familyId,
      masteryScore: mastery.masteryScore,
      unlocked: mastery.masteryScore >= 60, // Need 60% to unlock challenges
    }))
    .sort((a, b) => b.masteryScore - a.masteryScore)
}
